import * as React from "react"
import {
  Box,
  Typography,
  Skeleton,
  useTheme,
  Avatar,
  BoxProps,
} from "@mui/material"
import NotListedLocationIcon from "@mui/icons-material/NotListedLocation"
import { deepmerge } from "@mui/utils"
import { NormalizedWeatherData } from "./types"

export interface IWeatherWidgetProps extends BoxProps {
  data?: NormalizedWeatherData
  loading?: boolean
  width?: number | string
}

/**
 * Compact weather card displaying temperature, humidity, and AQI (1–5 scale).
 */
export const WeatherWidget: React.FC<IWeatherWidgetProps> = ({
  data,
  loading = false,
  sx = {},
  ...props
}) => {
  const theme = useTheme()

  const {
    root,
    mainSection,
    mainHighlightText,
    infoListContainer,
    infoListItem,
    labelTextContainer,
    highlightText,
    labelText,
    subLabelText,
    image,
    fallbackImage,
  } = theme.components[`WeatherWidget`]?.styleOverrides || {}

  if (loading) {
    return (
      <Box sx={deepmerge(root, sx)} {...props}>
        <Box sx={mainSection}>
          <Skeleton variant="rounded" height={56} width={56} />
          <Skeleton variant="rounded" height={56} width={127} />
        </Box>
        <Box sx={infoListContainer}>
          <Skeleton variant="text" height={40} width={"100%"} />
          <Skeleton variant="text" height={40} width={"100%"} />
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={deepmerge(root, sx)} {...props}>
      <Box sx={mainSection}>
        <Avatar sx={image} src={data.iconUrl} alt={data.condition}>
          <NotListedLocationIcon sx={fallbackImage} />
        </Avatar>
        <Typography variant="h4" sx={mainHighlightText}>
          {Math.round(data?.temperature ?? 0)}
          <sup>°C</sup>
        </Typography>
      </Box>

      <Box sx={infoListContainer}>
        <Box sx={infoListItem}>
          <Typography sx={highlightText}>{data?.humidity ?? 0}%</Typography>
          <Typography sx={labelText}>Humidity</Typography>
        </Box>
        <Box sx={infoListItem}>
          <Typography sx={highlightText} className={data?.usAqiRef}>
            {data?.usAqiValue ?? 0}
          </Typography>
          <Box sx={labelTextContainer}>
            <Typography sx={labelText}>US AQI+</Typography>
            <Typography
              variant="caption"
              className={data?.usAqiRef}
              sx={subLabelText}
            >
              {data?.usAqiLevelConcern}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
